import { all, call, put, takeLatest, select, take } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { SELECT_DEALER } from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';

import * as detailsActions from 'modules/interventions/actions/details';
import * as mobilityActions from 'modules/interventions/actions/mobility';
import * as types from 'modules/interventions/actionTypes';

import {
  isMobilityModalOpen,
  getModalSelfService,
  getBaseRequestParams,
  getSelectedInterventionId,
} from 'modules/interventions/selectors/mobilityDashboard';
import { shouldResendSelfService } from 'modules/interventions/utils';
import { createNotification } from 'modules/toast-notifications/actions';

function* updateMobilityWorker({ payload: { request }, meta }) {
  let type = 'success';
  let message = { id: 'checkIn.mobility.update.success', defaultMessage: 'Mobility updated' };

  const { data: updatedMobility } = request;

  const selfService = yield select(getModalSelfService);
  const shouldResend = yield call(shouldResendSelfService, selfService, updatedMobility);

  try {
    const { data } = yield call(restClient.request, request);
    yield put(mobilityActions.updateMobilitySuccess(data, shouldResend ? { action: 'resend', ...meta } : meta));
  } catch (error) {
    type = 'error';
    message = { id: 'http.error.message', defaultMessage: 'Oops! Something went wrong.' };
    yield put(mobilityActions.updateMobilityFailure(error, meta));
  }
  yield put(createNotification({ type, message }));
}

function* refreshMobilityDashboardItem(interventionId, params = {}) {
  yield put(mobilityActions.getMobilityEntries({ ...params, page: 0, size: 1, ids: [interventionId] }));
  const waitActions = [types.FETCH_MOBILITY_INTERVENTIONS_SUCCESS, types.FETCH_MOBILITY_INTERVENTIONS_FAILURE];
  const { error, payload } = yield take(waitActions);

  if (!error && payload.content.length === 0) {
    // In this case, the {interventionId} loaded should not appear in the dashboard view
    yield put(mobilityActions.removeFromDashboard(interventionId));
  }
}

function* refreshDashboard() {
  const params = yield select(state => getBaseRequestParams(state, false));
  yield putAndWait(mobilityActions.getMobilityEntries({ ...params, page: 0 }, { source: 'mobility-dashboard' }));
}

function* openMobilityModalWorker({ payload: { interventionId, mobilityId } }) {
  yield put(mobilityActions.fetchMobility(mobilityId));
  const { error, payload: mobility } = yield take([
    types.FETCH_MOBILITY_ITEM_SUCCESS,
    types.FETCH_MOBILITY_ITEM_FAILURE,
  ]);

  if (!error) {
    const params = yield select(state => getBaseRequestParams(state, true));
    yield call(refreshMobilityDashboardItem, interventionId, params);
    yield put(detailsActions.openMobilityModalReady({ ...mobility, interventionId }));
  }
}

function* afterUpdateMobility({ meta }) {
  const open = yield select(isMobilityModalOpen);
  if (open) {
    yield put(detailsActions.closeMobilityModal(meta));
  }
  if (meta?.action === 'resend') {
    yield put(detailsActions.openResendModal(meta));
  }
}

function* handleRefreshMobilityDashboardItem() {
  const params = yield select(getBaseRequestParams);
  const interventionId = yield select(getSelectedInterventionId);

  yield call(refreshMobilityDashboardItem, interventionId, params);
}

const onFilterChange = ({ type }) =>
  [
    SELECT_DEALER,
    types.SET_MOBILITY_INTERVENTION_FILTER,
    types.SET_MOBILITY_INTERVENTION_FILTERS,
    types.CLEAR_MOBILITY_INTERVENTION_FILTER,
    types.REMOVE_MOBILITY_INTERVENTION_FILTER,
  ].includes(type);

function* onPageWorker() {
  yield call(refreshDashboard);
  yield takeLatest(onFilterChange, refreshDashboard);
  yield takeLatest(types.UPDATE_MOBILITY_ITEM_SUCCESS, handleRefreshMobilityDashboardItem);
}

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.FETCH_MOBILITY_INTERVENTIONS]: {},
  [types.FETCH_MOBILITY_ITEM]: {},
};

export default function* interventionsMobilitySagas() {
  // Simple requests
  yield all(simpleRequestsLoader(simpleRequests));

  // Actions
  yield scopeToPage([PATH.SELF_SERVICE_MOBILITY], onPageWorker);
  yield takeLatest(types.OPEN_MOBILITY_MODAL, openMobilityModalWorker);
  yield takeLatest(types.UPDATE_MOBILITY_ITEM_SUCCESS, afterUpdateMobility);

  // Requests
  yield takeLatest(types.UPDATE_MOBILITY_ITEM, updateMobilityWorker);
}
